import PropTypes from 'prop-types'
import React, { useRef, useState, useEffect } from 'react'
import format from 'date-fns/format'

import { Input } from '../input'

import { CalendarIcon } from './calendarIcon'
import styles from './datePicker.module.scss'
import { CalendarInput } from 'app/components/common/calendarInput'
import { FormControl } from '../formControl'
import formatISO from 'date-fns/formatISO'

const DATE_FORMAT = 'MM/dd/yyyy'

const DatePicker = ({
  className,
  type,
  idFrom,
  idTo,
  nameFrom,
  nameTo,
  refFrom,
  refTo,
  errorFrom,
  errorTo,
  resetTrigger,
  ...rest
}) => {
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [focusedInput, setFocusedInput] = useState(null)
  const [days, setDays] = useState({ startDay: '', endDay: '' })
  const fromRef = useRef()
  const toRef = useRef()

  useEffect(() => {
    setDays({ startDay: '', endDay: '' })
  }, [resetTrigger])

  const toggleCalendarOpen = (isOpen) => {
    if (!isOpen) {
      setFocusedInput(null)
    }

    setCalendarOpen(isOpen)
  }

  const openCalendar = (e) => {
    e.preventDefault()

    toggleCalendarOpen(true)
  }

  const suffix = (inputIndex) => {
    const clickable = type !== 'OW' || inputIndex === 0

    const onClick = (e) => {
      if (calendarOpen) {
        if (focusedInput === inputIndex) {
          setFocusedInput(null)
          toggleCalendarOpen(false)
        } else {
          setFocusedInput(inputIndex)
        }

        return
      }

      setFocusedInput(inputIndex)
      openCalendar(e)
    }

    return {
      width: '60px',
      template: (
        <CalendarIcon
          className={clickable ? styles.datePicker__icon : ''}
          onClick={clickable ? onClick : null}
        />
      ),
    }
  }

  const startDayString = days.startDay ? format(days.startDay, DATE_FORMAT) : ''
  const endDayString =
    type === 'OW' ? 'One Way' : days.endDay ? format(days.endDay, DATE_FORMAT) : ''

  const formValues = {
    from: days.startDay ? formatISO(days.startDay, { representation: 'date' }) : '',
    to: days.endDay ? formatISO(days.endDay, { representation: 'date' }) : '',
  }

  const calendar = (
    <CalendarInput
      type={type}
      overlayRefs={[fromRef, toRef]}
      open={calendarOpen}
      toggleOpen={toggleCalendarOpen}
      setDays={(newDays) => {
        if ((newDays.startDay && newDays.endDay) || (type === 'OW' && newDays.startDay)) {
          if (focusedInput === 0 && !days.endDay && type !== 'OW') {
            setFocusedInput(1)
            setDays(newDays)
            return
          }

          toggleCalendarOpen(false)
        } else if (newDays.startDay && !newDays.endDay) {
          setFocusedInput(1)
        }

        setDays(newDays)
      }}
      mode={focusedInput === 1 ? 'end' : 'start'}
    />
  )

  return (
    <>
      <input type="hidden" name={nameFrom} ref={refFrom} defaultValue={formValues.from} />
      <input type="hidden" name={nameTo} ref={refTo} defaultValue={formValues.to} />
      <FormControl ref={fromRef}>
        <Input
          id={idFrom}
          className={`${styles.datePicker} ${className}`}
          inputClass={`${styles.withPadding} ${
            focusedInput === 0 ? styles.datePicker__inputFocused : ''
          }`}
          suffix={suffix(0)}
          label="Departure Date"
          onFocus={(e) => {
            setFocusedInput(0)
            openCalendar(e)
          }}
          value={startDayString}
          readOnly
          error={errorFrom}
          {...rest}
        ></Input>
        {calendar}
      </FormControl>
      <FormControl ref={toRef}>
        <Input
          id={idTo}
          className={`${styles.datePicker} ${className}`}
          inputClass={`${styles.withPadding} ${
            focusedInput === 1 ? styles.datePicker__inputFocused : ''
          }`}
          suffix={suffix(1)}
          label="Return Date"
          disabled={type === 'OW'}
          onFocus={(e) => {
            setFocusedInput(1)
            openCalendar(e)
          }}
          value={endDayString}
          readOnly
          error={errorTo}
          {...rest}
        ></Input>
      </FormControl>
    </>
  )
}

DatePicker.displayName = 'DatePicker'

DatePicker.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  type: PropTypes.oneOf(['OW', 'RT']).isRequired,
  idFrom: PropTypes.string,
  idTo: PropTypes.string,
  nameFrom: PropTypes.string,
  nameTo: PropTypes.string,
  refFrom: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.elementType }),
  ]),
  refTo: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.elementType })]),
  errorFrom: PropTypes.string,
  errorTo: PropTypes.string,
  resetTrigger: PropTypes.number,
}

export { DatePicker }
